import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Phone, PhoneCall, PhoneOff, Bot, Loader2, CheckCircle, XCircle,
  Clock, ArrowRight, Zap, Target, Volume2, TrendingUp,
  DollarSign, BarChart3, Filter, RefreshCw, ChevronDown, ChevronRight,
  User, Building2, ExternalLink, CalendarClock, X, History, Voicemail
} from 'lucide-react';
import { voiceAPI } from '../../lib/api';

const CALL_TYPES = {
  discovery: { label: 'Discovery', icon: Target, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200' },
  demo_scheduling: { label: 'Demo', icon: Zap, color: 'text-purple-600', bg: 'bg-purple-100', border: 'border-purple-200' },
  followup: { label: 'Follow-up', icon: ArrowRight, color: 'text-amber-600', bg: 'bg-amber-100', border: 'border-amber-200' },
  closing: { label: 'Closing', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200' },
  re_engagement: { label: 'Re-engage', icon: Phone, color: 'text-teal-600', bg: 'bg-teal-100', border: 'border-teal-200' },
};

const STATUS_CONFIG = {
  initiated: { label: 'Initiated', icon: Phone, cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  ringing: { label: 'Ringing', icon: PhoneCall, cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  'in-progress': { label: 'In Progress', icon: Volume2, cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  completed: { label: 'Completed', icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  failed: { label: 'Failed', icon: XCircle, cls: 'bg-red-100 text-red-800 border-red-200' },
  'no-answer': { label: 'No Answer', icon: PhoneOff, cls: 'bg-muted text-muted-foreground border-border' },
};

const OUTCOME_CONFIG = {
  positive: { label: 'Positive', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  negative: { label: 'Negative', cls: 'bg-red-100 text-red-800 border-red-200' },
  neutral: { label: 'Neutral', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

const FAILED_STATUSES = ['failed', 'no-answer', 'busy', 'canceled', 'cancelled'];

// Translate a raw telephony/SIP error into a plain, human reason the user can act on.
function explainFailure(call) {
  const raw = (call?.error && call.error !== 'None') ? String(call.error) : '';
  const low = raw.toLowerCase();
  const status = call?.status;

  if (status === 'no-answer' && !raw) {
    return { reason: "Lead didn't pick up", hint: 'The number rang but was not answered. Retry at a better time.', raw: '' };
  }
  if (status === 'busy' && !raw) {
    return { reason: 'Line was busy', hint: 'The number was on another call. Retry shortly.', raw: '' };
  }
  if (status === 'canceled' || status === 'cancelled') {
    return { reason: 'Call cancelled', hint: 'This call was cancelled before it connected.', raw };
  }
  if (low.includes('not yet verified') || low.includes('not verified')) {
    return { reason: 'Caller number not verified', hint: 'The outbound (caller) number is not yet verified with the carrier. Verify/approve the DID in telephony settings.', raw };
  }
  if (low.includes('480') || low.includes('temporarily unavailable') || low.includes('unavailable') || low.includes('408')) {
    return { reason: "Number didn't answer / unavailable", hint: 'The destination was unreachable or did not answer (SIP 480/408). Retry later.', raw };
  }
  if (low.includes('603') || low.includes('declined')) {
    return { reason: 'Carrier declined the call', hint: 'Reached the carrier but it rejected the call (SIP 603) — usually outbound/DLT not enabled on the DID, or the number is on DND.', raw };
  }
  if (low.includes('486') || low.includes('busy')) {
    return { reason: 'Line was busy', hint: 'The destination was busy (SIP 486). Retry shortly.', raw };
  }
  if (low.includes('404') || low.includes('does not match any sip trunk')) {
    return { reason: 'SIP trunk not matched', hint: 'The carrier could not match the trunk (SIP 404). Re-apply outbound routing or allowlist egress IPs.', raw };
  }
  if (low.includes('not enough credits') || low.includes('credit')) {
    return { reason: 'Not enough credits', hint: 'Top up credits in Billing & Plans to place calls.', raw };
  }
  if (low.includes('trai')) {
    return { reason: 'Outside TRAI calling window', hint: 'Calls to Indian numbers are only allowed 10:00–21:00 IST.', raw };
  }
  if (raw) {
    return { reason: 'Call failed', hint: raw, raw };
  }
  return { reason: 'Call failed', hint: 'The call did not connect. Retry or check telephony settings.', raw: '' };
}

export default function CallHistoryDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ calls: [], total: 0, stats: {} });
  const [selectedCall, setSelectedCall] = useState(null);
  const [activeTab, setActiveTab] = useState('history');
  const [queue, setQueue] = useState({ calls: [], total: 0, stats: {} });
  const [queueLoading, setQueueLoading] = useState(false);

  // Filters
  const [callType, setCallType] = useState('all');
  const [status, setStatus] = useState('all');
  const [outcome, setOutcome] = useState('all');
  const [reach, setReach] = useState('all');

  const fetchDashboard = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const params = {};
      if (callType !== 'all') params.call_type = callType;
      if (status !== 'all') params.status = status;
      if (outcome !== 'all') params.outcome = outcome;
      if (reach !== 'all') params.reach = reach;
      params.limit = 100;

      const res = await voiceAPI.getBrainCallsDashboard(params);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load call history');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [callType, status, outcome, reach]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetchQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const res = await voiceAPI.getCallQueue({ limit: 50 });
      setQueue(res.data);
    } catch { /* ignore */ }
    finally { setQueueLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'scheduled') fetchQueue(); }, [activeTab, fetchQueue]);

  const handleCancelCall = async (queueId) => {
    try {
      await voiceAPI.cancelScheduledCall(queueId);
      toast.success('Scheduled call cancelled');
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const stats = data.stats || {};

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="call-history-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-4"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="call-history-dashboard">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Bot className="h-7 w-7 text-violet-600" />
            AI Call History
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Bird's-eye view of all autonomous AI Brain calls across your leads
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchDashboard(true)}
          disabled={refreshing}
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs: History / Scheduled */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 lg:w-[320px]">
          <TabsTrigger value="history" className="gap-2" data-testid="tab-history">
            <History className="h-4 w-4" />
            Call History
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="gap-2" data-testid="tab-scheduled">
            <CalendarClock className="h-4 w-4" />
            Scheduled
            {(queue.stats?.scheduled || 0) > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs h-5 min-w-5 px-1">{queue.stats.scheduled}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ========= HISTORY TAB ========= */}
        <TabsContent value="history" className="space-y-5">

      {/* Stats Grid */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-6">
        <StatCard
          title="Total Calls"
          value={stats.total_calls || 0}
          icon={Phone}
          color="text-violet-600"
          bg="bg-violet-50"
          testId="stat-total-calls"
        />
        <StatCard
          title="Connected"
          value={stats.connected || 0}
          icon={CheckCircle}
          color="text-emerald-600"
          bg="bg-emerald-50"
          testId="stat-completed"
        />
        <StatCard
          title="Voicemails"
          value={stats.voicemails || 0}
          icon={Voicemail}
          color="text-orange-600"
          bg="bg-orange-50"
          testId="stat-voicemails"
        />
        <StatCard
          title="Active"
          value={stats.in_progress || 0}
          icon={PhoneCall}
          color="text-blue-600"
          bg="bg-blue-50"
          testId="stat-active"
        />
        <StatCard
          title="Conversion"
          value={`${stats.conversion_rate || 0}%`}
          icon={TrendingUp}
          color="text-green-600"
          bg="bg-green-50"
          testId="stat-conversion"
        />
        <StatCard
          title="Credits Used"
          value={`$${(stats.total_credits_spent || 0).toFixed(2)}`}
          icon={DollarSign}
          color="text-amber-600"
          bg="bg-amber-50"
          testId="stat-credits"
        />
      </div>

      {/* Breakdown Row */}
      {stats.total_calls > 0 && (
        <div className="grid gap-4 lg:grid-cols-3">
          <BreakdownCard title="By Call Type" data={stats.type_breakdown || {}} config={CALL_TYPES} total={stats.total_calls} />
          <BreakdownCard title="By Status" data={stats.status_breakdown || {}} config={STATUS_CONFIG} total={stats.total_calls} isStatus />
          <BreakdownCard title="By Outcome" data={stats.outcome_breakdown || {}} config={OUTCOME_CONFIG} total={stats.total_calls} isOutcome />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          Filters:
        </div>
        <Select value={callType} onValueChange={setCallType}>
          <SelectTrigger className="w-[150px]" data-testid="filter-call-type">
            <SelectValue placeholder="Call Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(CALL_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[150px]" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={outcome} onValueChange={setOutcome}>
          <SelectTrigger className="w-[150px]" data-testid="filter-outcome">
            <SelectValue placeholder="Outcome" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Outcomes</SelectItem>
            {Object.entries(OUTCOME_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={reach} onValueChange={setReach}>
          <SelectTrigger className="w-[170px]" data-testid="filter-reach">
            <SelectValue placeholder="Reach" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reach</SelectItem>
            <SelectItem value="connected" data-testid="filter-reach-connected">Connected (human)</SelectItem>
            <SelectItem value="voicemail" data-testid="filter-reach-voicemail">Voicemail{stats.voicemails ? ` (${stats.voicemails})` : ''}</SelectItem>
            <SelectItem value="not_reached" data-testid="filter-reach-not-reached">Not reached</SelectItem>
          </SelectContent>
        </Select>
        {(callType !== 'all' || status !== 'all' || outcome !== 'all' || reach !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setCallType('all'); setStatus('all'); setOutcome('all'); setReach('all'); }} data-testid="clear-filters-btn">
            Clear Filters
          </Button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          Showing {data.calls.length} of {data.total} calls
        </span>
      </div>

      {/* Call Table */}
      {data.calls.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16" data-testid="call-history-empty">
            <PhoneOff className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <h3 className="text-lg font-medium mb-1">No AI Brain calls yet</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Reveal a lead's phone in Pipeline, then click <span className="font-medium">AI Call</span> on the lead detail page. The Brain will dial, transcribe, and score the conversation autonomously.
            </p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => window.location.href = '/leads'} data-testid="callhistory-go-pipeline-btn">
                Go to Pipeline
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="calls-table">
          <ScrollArea className="h-[520px]">
            <div className="min-w-[800px]">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b bg-muted/40 text-xs font-semibold uppercase text-muted-foreground sticky top-0 z-10">
                <div className="col-span-3">Lead</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1">Outcome</div>
                <div className="col-span-2">Summary</div>
                <div className="col-span-2 text-right">Date</div>
              </div>
              {/* Rows */}
              {data.calls.map((call) => {
                const typeInfo = CALL_TYPES[call.call_type] || CALL_TYPES.discovery;
                const statusInfo = STATUS_CONFIG[call.status] || STATUS_CONFIG.initiated;
                const outcomeInfo = call.outcome ? (OUTCOME_CONFIG[call.outcome] || {}) : null;
                const StatusIcon = statusInfo.icon;
                const isActive = ['initiated', 'ringing', 'in-progress'].includes(call.status);

                return (
                  <div
                    key={call.id}
                    className={`grid grid-cols-12 gap-2 px-4 py-3 border-b hover:bg-muted/30 cursor-pointer transition-colors items-center ${isActive ? 'bg-violet-50/50' : ''}`}
                    onClick={() => setSelectedCall(call)}
                    data-testid={`call-row-${call.id}`}
                  >
                    {/* Lead */}
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{call.lead_name || 'Unknown'}</p>
                        {call.lead_company && <p className="text-xs text-muted-foreground truncate">{call.lead_company}</p>}
                      </div>
                    </div>
                    {/* Type */}
                    <div className="col-span-2">
                      <Badge variant="outline" className={`text-xs ${typeInfo.bg} ${typeInfo.color} ${typeInfo.border}`}>
                        {typeInfo.label}
                      </Badge>
                    </div>
                    {/* Status */}
                    <div className="col-span-2 flex items-center gap-1.5">
                      <Badge className={`text-xs ${statusInfo.cls}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusInfo.label}
                      </Badge>
                      {call.is_voicemail && (
                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200" data-testid={`call-voicemail-badge-${call.id}`}>
                          <Voicemail className="h-3 w-3 mr-1" />Voicemail
                        </Badge>
                      )}
                      {isActive && <Loader2 className="h-3 w-3 animate-spin text-violet-500" />}
                    </div>
                    {/* Outcome */}
                    <div className="col-span-1">
                      {outcomeInfo ? (
                        <Badge variant="outline" className={`text-xs ${outcomeInfo.cls}`}>
                          {outcomeInfo.label}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                    {/* Summary */}
                    <div className="col-span-2">
                      {FAILED_STATUSES.includes(call.status) ? (
                        <div className="flex items-start gap-1 text-red-600" data-testid={`call-failure-${call.id}`}>
                          <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                          <p className="text-xs font-medium line-clamp-2">{explainFailure(call).reason}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground line-clamp-2">{call.ai_summary || call.outcome_summary || '—'}</p>
                      )}
                    </div>
                    {/* Date */}
                    <div className="col-span-2 text-right">
                      <p className="text-xs text-muted-foreground">
                        {call.created_at ? format(new Date(call.created_at), 'MMM dd, HH:mm') : '—'}
                      </p>
                      {call.duration_seconds > 0 && (
                        <p className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                          <Clock className="h-3 w-3" />{call.duration_seconds}s
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </Card>
      )}

      {/* Call Detail Dialog */}
      {selectedCall && (
        <CallDetailDialog
          call={selectedCall}
          open={!!selectedCall}
          onOpenChange={(open) => { if (!open) setSelectedCall(null); }}
          onViewLead={(leadId) => navigate(`/leads/${leadId}`)}
        />
      )}

        </TabsContent>

        {/* ========= SCHEDULED TAB ========= */}
        <TabsContent value="scheduled" className="space-y-5">
          <ScheduledCallsTab
            queue={queue}
            loading={queueLoading}
            onCancel={handleCancelCall}
            onRefresh={fetchQueue}
            navigate={navigate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ========================
// SUB-COMPONENTS
// ========================

function StatCard({ title, value, icon: Icon, color, bg, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownCard({ title, data, config, total, isStatus, isOutcome }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-2">
        {entries.map(([key, count]) => {
          const pct = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
          const info = config[key] || {};
          return (
            <div key={key} className="flex items-center gap-2 text-sm">
              <div className="w-20 truncate text-xs font-medium">
                {info.label || key}
              </div>
              <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${isStatus ? 'bg-slate-500' : isOutcome ? (key === 'positive' ? 'bg-emerald-500' : key === 'negative' ? 'bg-red-500' : 'bg-amber-500') : 'bg-violet-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-12 text-right">{count} ({pct}%)</span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

const QUEUE_STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-800 border-blue-200', icon: CalendarClock },
  processing: { label: 'Processing', cls: 'bg-violet-100 text-violet-800 border-violet-200', icon: Loader2 },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-muted text-muted-foreground border-border', icon: X },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
};

function ScheduledCallsTab({ queue, loading, onCancel, onRefresh, navigate }) {
  const qStats = queue.stats || {};
  const [qStatus, setQStatus] = useState('all');
  const [qSearch, setQSearch] = useState('');

  if (loading) {
    return (
      <div className="space-y-4" data-testid="scheduled-calls-skeleton">
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 rounded-lg border space-y-2">
              <div className="h-3 w-16 bg-muted/60 rounded animate-pulse" />
              <div className="h-7 w-10 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-4 border rounded-lg">
            <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 bg-muted rounded animate-pulse" />
              <div className="h-3 w-48 bg-muted/40 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const allCalls = queue.calls || [];
  const filteredCalls = allCalls.filter((item) => {
    if (qStatus !== 'all' && item.status !== qStatus) return false;
    if (qSearch.trim()) {
      const q = qSearch.trim().toLowerCase();
      const hay = `${item.lead_name || ''} ${item.lead_company || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const hasFilter = qStatus !== 'all' || qSearch.trim() !== '';

  return (
    <div className="space-y-4" data-testid="scheduled-calls-tab">
      {/* Queue Stats */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <StatCard title="Scheduled" value={qStats.scheduled || 0} icon={CalendarClock} color="text-blue-600" bg="bg-blue-50" testId="stat-queue-scheduled" />
        <StatCard title="Processing" value={qStats.processing || 0} icon={Loader2} color="text-violet-600" bg="bg-violet-50" testId="stat-queue-processing" />
        <StatCard title="Completed" value={qStats.completed || 0} icon={CheckCircle} color="text-emerald-600" bg="bg-emerald-50" testId="stat-queue-completed" />
        <StatCard title="Failed" value={qStats.failed || 0} icon={XCircle} color="text-red-600" bg="bg-red-50" testId="stat-queue-failed" />
        <StatCard title="Cancelled" value={qStats.cancelled || 0} icon={X} color="text-gray-600" bg="bg-gray-50" testId="stat-queue-cancelled" />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          Filters:
        </div>
        <Select value={qStatus} onValueChange={setQStatus}>
          <SelectTrigger className="w-[160px]" data-testid="filter-queue-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(QUEUE_STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          placeholder="Search lead or company…"
          value={qSearch}
          onChange={(e) => setQSearch(e.target.value)}
          className="w-[220px] h-9"
          data-testid="filter-queue-search"
        />
        {hasFilter && (
          <Button variant="ghost" size="sm" onClick={() => { setQStatus('all'); setQSearch(''); }} data-testid="clear-queue-filters-btn">
            Clear Filters
          </Button>
        )}
        <span className="text-xs text-muted-foreground">
          Showing {filteredCalls.length} of {allCalls.length}
        </span>
        <Button variant="outline" size="sm" onClick={onRefresh} className="ml-auto" data-testid="refresh-queue-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh Queue
        </Button>
      </div>

      {/* Queue Table */}
      {allCalls.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CalendarClock className="h-12 w-12 text-muted-foreground mb-3 opacity-50" />
            <h3 className="text-lg font-medium mb-1">No Scheduled Calls</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Go to a lead's detail page and click "AI Call" → "Schedule" to queue calls at optimal times.
            </p>
          </CardContent>
        </Card>
      ) : filteredCalls.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12" data-testid="queue-no-match">
            <Filter className="h-10 w-10 text-muted-foreground mb-3 opacity-40" />
            <h3 className="text-base font-medium mb-1">No calls match your filters</h3>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => { setQStatus('all'); setQSearch(''); }}>
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="queue-table">
          <ScrollArea className="h-[420px]">
            <div className="min-w-[700px]">
              <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b bg-muted/40 text-xs font-semibold uppercase text-muted-foreground sticky top-0 z-10">
                <div className="col-span-3">Lead</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-3">Scheduled At</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {filteredCalls.map((item) => {
                const typeInfo = CALL_TYPES[item.call_type] || {};
                const statusInfo = QUEUE_STATUS_CONFIG[item.status] || QUEUE_STATUS_CONFIG.scheduled;
                const StatusIcon = statusInfo.icon;
                return (
                  <div key={item.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b hover:bg-muted/30 items-center" data-testid={`queue-row-${item.id}`}>
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{item.lead_name || 'Unknown'}</p>
                        {item.lead_company && <p className="text-xs text-muted-foreground truncate">{item.lead_company}</p>}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Badge variant="outline" className={`text-xs ${typeInfo.bg || ''} ${typeInfo.color || ''}`}>
                        {typeInfo.label || item.call_type || 'Auto'}
                      </Badge>
                    </div>
                    <div className="col-span-2">
                      <Badge className={`text-xs ${statusInfo.cls}`}>
                        <StatusIcon className={`h-3 w-3 mr-1 ${item.status === 'processing' ? 'animate-spin' : ''}`} />
                        {statusInfo.label}
                      </Badge>
                    </div>
                    <div className="col-span-3">
                      <p className="text-sm">
                        {item.scheduled_at ? format(new Date(item.scheduled_at), 'MMM dd, h:mm a') : '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.timezone} · {item.timing_source === 'engagement_history' ? 'Based on engagement' : item.timing_source === 'manual' ? 'Manual' : 'Optimal window'}
                      </p>
                    </div>
                    <div className="col-span-2 flex justify-end gap-1">
                      {item.lead_id && (
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigate(`/leads/${item.lead_id}`)}>
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                      {item.status === 'scheduled' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => onCancel(item.id)}
                          data-testid={`cancel-queue-${item.id}`}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
}

function CallDetailDialog({ call, open, onOpenChange, onViewLead }) {
  const c = call;
  const typeInfo = CALL_TYPES[c.call_type] || CALL_TYPES.discovery;
  const statusInfo = STATUS_CONFIG[c.status] || STATUS_CONFIG.initiated;
  const StatusIcon = statusInfo.icon;
  const brief = c.brief || {};
  const [briefOpen, setBriefOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="call-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-violet-600" />
            {typeInfo.label} Call — {c.lead_name || 'Unknown'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status */}
          <div className={`rounded-lg p-3 flex items-center justify-between ${c.status === 'completed' ? 'bg-emerald-50 border border-emerald-200' : c.status === 'failed' || c.status === 'no-answer' ? 'bg-red-50 border border-red-200' : 'bg-violet-50 border border-violet-200'}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className="h-5 w-5" />
              <div>
                <p className="font-medium text-sm">{statusInfo.label}</p>
                <p className="text-xs text-muted-foreground">{c.to_number} · {c.mode} mode</p>
              </div>
            </div>
            {c.duration_seconds > 0 && (
              <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />{c.duration_seconds}s</Badge>
            )}
          </div>

          {/* Why it failed */}
          {FAILED_STATUSES.includes(c.status) && (() => {
            const f = explainFailure(c);
            return (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="call-failure-detail">
                <p className="text-xs font-semibold uppercase text-red-700 mb-1 flex items-center gap-1">
                  <XCircle className="h-3.5 w-3.5" /> Why it failed
                </p>
                <p className="text-sm font-medium text-red-800">{f.reason}</p>
                {f.hint && <p className="text-xs text-red-700/90 mt-1">{f.hint}</p>}
                {f.raw && f.raw !== f.hint && (
                  <p className="text-[11px] text-red-600/70 mt-2 font-mono break-words line-clamp-3">{f.raw}</p>
                )}
              </div>
            );
          })()}

          {/* Lead Info */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">{c.lead_name || 'Unknown'}</p>
                {c.lead_company && <p className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3 w-3" />{c.lead_company}</p>}
              </div>
            </div>
            {c.lead_id && (
              <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); onViewLead(c.lead_id); }} data-testid="view-lead-btn">
                <ExternalLink className="h-3 w-3 mr-1" /> View Lead
              </Button>
            )}
          </div>

          {/* AI Summary */}
          {(c.ai_summary || c.outcome_summary) && (
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">AI Summary</p>
              <p className="text-sm bg-muted/30 rounded-lg p-3">{c.ai_summary || c.outcome_summary}</p>
            </div>
          )}

          {/* Outcome & Next Action */}
          {c.outcome && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Outcome</p>
                <Badge variant="outline" className={`capitalize ${c.outcome === 'positive' ? 'border-emerald-300 text-emerald-700' : c.outcome === 'negative' ? 'border-red-300 text-red-700' : 'border-amber-300 text-amber-700'}`}>
                  {c.outcome}
                </Badge>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Next Action</p>
                <p className="text-sm">{c.next_action || 'N/A'}</p>
              </div>
            </div>
          )}

          {/* Key Insights */}
          {c.key_insights && c.key_insights.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Key Insights</p>
              <div className="flex flex-wrap gap-1.5">
                {c.key_insights.map((insight, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{insight}</Badge>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Brief (collapsible) */}
          {(brief.greeting || brief.purpose_statement) && (
            <div>
              <button
                className="flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setBriefOpen(!briefOpen)}
              >
                {briefOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Call Brief
              </button>
              {briefOpen && (
                <div className="mt-2 space-y-2 bg-muted/30 rounded-lg p-3 text-sm">
                  {brief.greeting && <div><span className="font-medium text-xs text-muted-foreground">Greeting:</span><p>{brief.greeting}</p></div>}
                  {brief.purpose_statement && <div><span className="font-medium text-xs text-muted-foreground">Purpose:</span><p>{brief.purpose_statement}</p></div>}
                  {brief.value_proposition && <div><span className="font-medium text-xs text-muted-foreground">Value Prop:</span><p>{brief.value_proposition}</p></div>}
                  {brief.closing_action && <div><span className="font-medium text-xs text-muted-foreground">Closing:</span><p>{brief.closing_action}</p></div>}
                </div>
              )}
            </div>
          )}

          {/* Meta */}
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="flex justify-between"><span>Call ID</span><span className="font-mono">{c.id?.slice(0, 16)}...</span></div>
            <div className="flex justify-between"><span>Credit Cost</span><span>${c.credit_cost?.toFixed(2) || '0.50'}</span></div>
            {c.created_at && <div className="flex justify-between"><span>Started</span><span>{format(new Date(c.created_at), 'MMM dd, yyyy HH:mm')}</span></div>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
